/** Wallets that can open this site inside their mobile in-app browser. */

export function isMobileDevice() {
  if (typeof navigator === "undefined" && typeof window === "undefined") return false;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(ua)) return true;
  if (typeof navigator !== "undefined" && navigator.userAgentData?.mobile) return true;
  if (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return isNarrowViewport() && prefersTouch();
}

/** Phone-width layout. Some in-app browsers report a desktop user agent. */
export function isNarrowViewport() {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(max-width: 1023px)").matches;
  }
  return typeof window.innerWidth === "number" && window.innerWidth > 0 && window.innerWidth < 1024;
}

function prefersTouch() {
  if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) return true;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(pointer: coarse)").matches;
}

/** Provider object the mobile wallet app injects into its in-app browser. */
export function getInjectedSolanaProvider(walletId) {
  if (typeof window === "undefined") return null;
  switch (walletId) {
    case "phantom":
      if (window.phantom?.solana) return window.phantom.solana;
      if (window.solana?.isPhantom) return window.solana;
      return null;
    case "solflare":
      if (window.solflare) return window.solflare;
      if (window.solana?.isSolflare) return window.solana;
      return null;
    case "backpack":
      if (window.backpack?.solana) return window.backpack.solana;
      if (window.backpack?.isBackpack) return window.backpack;
      if (window.solana?.isBackpack) return window.solana;
      return null;
    default:
      return null;
  }
}

/** In-app browsers put the wallet name in the user agent before the provider object exists. */
export function isWalletAppUserAgent(walletId) {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (walletId === "phantom") return /Phantom/i.test(ua);
  if (walletId === "solflare") return /Solflare/i.test(ua);
  if (walletId === "backpack") return /Backpack/i.test(ua);
  return false;
}

export function hasMobileAppWallet(walletId) {
  return Boolean(getInjectedSolanaProvider(walletId)) || isWalletAppUserAgent(walletId);
}

const INJECTED_WALLET_EVENTS = [
  "phantom#initialized",
  "solflare#initialized",
  "backpack#initialized",
];

/** Resolve a provider that may be injected a moment after the in-app page loads. */
export function waitForInjectedSolanaProvider(walletId, timeoutMs = 2500) {
  const existing = getInjectedSolanaProvider(walletId);
  if (existing || typeof window === "undefined") return Promise.resolve(existing);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (provider) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearInterval(poll);
      INJECTED_WALLET_EVENTS.forEach((name) => window.removeEventListener(name, onReady));
      resolve(provider || null);
    };
    const onReady = () => finish(getInjectedSolanaProvider(walletId));
    const timer = window.setTimeout(() => finish(getInjectedSolanaProvider(walletId)), timeoutMs);
    const poll = window.setInterval(() => {
      const provider = getInjectedSolanaProvider(walletId);
      if (provider) finish(provider);
    }, 200);
    INJECTED_WALLET_EVENTS.forEach((name) => window.addEventListener(name, onReady));
  });
}

/**
 * Universal link that loads `pageUrl` inside the wallet app.
 * @param {string} walletId
 * @param {string} pageUrl
 * @param {string} refOrigin
 */
export function walletInAppBrowseUrl(walletId, pageUrl, refOrigin) {
  const target = typeof pageUrl === "string" ? pageUrl.trim() : "";
  const ref = typeof refOrigin === "string" ? refOrigin.trim() : "";
  if (!target || !ref) return null;
  const encodedTarget = encodeURIComponent(target);
  const encodedRef = encodeURIComponent(ref);
  switch (walletId) {
    case "phantom":
      return `https://phantom.app/ul/browse/${encodedTarget}?ref=${encodedRef}`;
    case "solflare":
      return `https://solflare.com/ul/v1/browse/${encodedTarget}?ref=${encodedRef}`;
    case "backpack":
      return `https://backpack.app/ul/v1/browse/${encodedTarget}?ref=${encodedRef}`;
    case "coinbase":
      return `https://go.cb-w.com/dapp?cb_url=${encodedTarget}`;
    case "metamask": {
      const hostAndPath = target.replace(/^https?:\/\//i, "");
      return hostAndPath ? `https://metamask.app.link/dapp/${hostAndPath}` : null;
    }
    default:
      return null;
  }
}

export function canOpenWalletInAppBrowser(walletId) {
  if (!isMobileDevice() || hasMobileAppWallet(walletId)) return false;
  if (typeof window === "undefined") return false;
  return Boolean(
    walletInAppBrowseUrl(walletId, window.location.href, window.location.origin),
  );
}

/** Navigate the phone into the wallet's browser. Returns false when connect should stay on this page. */
export function openWalletInAppBrowser(walletId) {
  if (!canOpenWalletInAppBrowser(walletId)) return false;
  const url = walletInAppBrowseUrl(
    walletId,
    window.location.href,
    window.location.origin,
  );
  if (!url) return false;
  window.location.assign(url);
  return true;
}
